import { useState, useEffect, useMemo } from "react";
import { Clock, Sunrise, Sun, Sunset, Moon, Users } from "lucide-react";

/**
 * useZmanimData — shared data/logic hook for all ZmanimWidget templates.
 *
 * @param {object} zmanimMap         - Map of zman keys to "HH:MM" time strings
 * @param {Array}  customScheduleItems - User-defined schedule entries
 * @param {object} hebrewDate        - Hebrew date info ({ hebrewShort, ... })
 * @param {string} prayerColor       - Accent color for prayer items (default navy)
 * @param {string} lessonColor       - Accent color for lesson/other items (default goldDark)
 * @param {object} iconMap           - Optional per-key icon overrides, e.g. { sofZmanShma: Hourglass }
 */
export function useZmanimData({
  zmanimMap = {},
  customScheduleItems = [],
  hebrewDate = null,
  prayerColor = "#1e3a5f",
  lessonColor = "#b8923e",
  iconMap = {},
}) {
  const [now, setNow] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  const dayName = now.toLocaleDateString("he-IL", { weekday: "long" });

  const dayWithHebrewDate = hebrewDate?.hebrewShort
    ? `${dayName} ${hebrewDate.hebrewShort}`
    : dayName;

  const scheduleItems = useMemo(() => {
    const ic = (key, def) => iconMap[key] || def;
    const ZMANIM_META = {
      alotHaShachar:            { title: "עלות השחר",                           icon: ic("alotHaShachar",            Sunrise) },
      misheyakir:               { title: "משיכיר (טלית/תפילין)",                icon: ic("misheyakir",               Sunrise) },
      misheyakirMachmir:        { title: "משיכיר מחמיר",                        icon: ic("misheyakirMachmir",        Sunrise) },
      sunrise:                  { title: "הנץ החמה",                            icon: ic("sunrise",                  Sun    ) },
      sofZmanShmaMGA:           { title: 'סוף זמן ק"ש — מג"א (90 במע׳)',        icon: ic("sofZmanShmaMGA",           Clock  ) },
      sofZmanShmaMGA16Point1:   { title: 'סוף זמן ק"ש — מג"א (72 במע׳)',        icon: ic("sofZmanShmaMGA16Point1",   Clock  ) },
      sofZmanShma:              { title: 'סוף זמן קריאת שמע — גר"א',            icon: ic("sofZmanShma",              Clock  ) },
      sofZmanTfillaMGA:         { title: 'סוף זמן תפילה — מג"א (90 במע׳)',       icon: ic("sofZmanTfillaMGA",         Clock  ) },
      sofZmanTfillaMGA16Point1: { title: 'סוף זמן תפילה — מג"א (72 במע׳)',       icon: ic("sofZmanTfillaMGA16Point1", Clock  ) },
      sofZmanTfilla:            { title: 'סוף זמן תפילה — גר"א',                icon: ic("sofZmanTfilla",            Clock  ) },
      chatzot:                  { title: "חצות היום",                            icon: ic("chatzot",                  Sun    ) },
      minchaGedola:             { title: 'מנחה גדולה — גר"א',                   icon: ic("minchaGedola",             Clock  ) },
      minchaGedolaMGA:          { title: 'מנחה גדולה — מג"א',                   icon: ic("minchaGedolaMGA",          Clock  ) },
      minchaKetana:             { title: 'מנחה קטנה — גר"א',                    icon: ic("minchaKetana",             Clock  ) },
      minchaKetanaMGA:          { title: 'מנחה קטנה — מג"א',                    icon: ic("minchaKetanaMGA",          Clock  ) },
      plagHaMincha:             { title: "פלג המנחה",                            icon: ic("plagHaMincha",             Clock  ) },
      sofZmanAchilatChametz:    { title: 'סוף זמן אכילת חמץ — גר"א',            icon: ic("sofZmanAchilatChametz",    Clock  ) },
      sofZmanBiurChametz:       { title: 'סוף זמן ביעור חמץ — גר"א',            icon: ic("sofZmanBiurChametz",       Clock  ) },
      sofZmanAchilatChametzMGA: { title: 'סוף זמן אכילת חמץ — מג"א (72 במע׳)', icon: ic("sofZmanAchilatChametzMGA", Clock  ) },
      sofZmanBiurChametzMGA:    { title: 'סוף זמן ביעור חמץ — מג"א (72 במע׳)', icon: ic("sofZmanBiurChametzMGA",    Clock  ) },
      sunset:                   { title: "שקיעה",                                icon: ic("sunset",                   Sunset ) },
      beinHaShmashos:           { title: "בין השמשות",                           icon: ic("beinHaShmashos",           Sunset ) },
      tzeit85deg:               { title: "צאת הכוכבים",                          icon: ic("tzeit85deg",               Moon   ) },
      tzeit7083deg:             { title: 'צאת כוכבים (ר"ת 7°)',                  icon: ic("tzeit7083deg",             Moon   ) },
      tzeit42min:               { title: "צאת כוכבים 42 דק'",                    icon: ic("tzeit42min",               Moon   ) },
      tzeit50min:               { title: "צאת כוכבים 50 דק'",                    icon: ic("tzeit50min",               Moon   ) },
      tzeit72min:               { title: "צאת כוכבים 72 דק'",                    icon: ic("tzeit72min",               Moon   ) },
      chatzotNight:             { title: "חצות הלילה",                           icon: ic("chatzotNight",             Moon   ) },
    };

    const base = Object.entries(zmanimMap)
      .filter(([key, time]) => time && time !== "--:--" && !key.endsWith("_tomorrow"))
      .map(([key, time]) => ({
        time,
        title: ZMANIM_META[key]?.title || key,
        icon:  ZMANIM_META[key]?.icon  || Clock,
        type:  "simple",
      }));

    const baseTomorrow = Object.entries(zmanimMap)
      .filter(([key, time]) => time && time !== "--:--" && key.endsWith("_tomorrow"))
      .map(([key, time]) => {
        const baseKey = key.replace("_tomorrow", "");
        return {
          time,
          title:      ZMANIM_META[baseKey]?.title || baseKey,
          icon:       ZMANIM_META[baseKey]?.icon  || Clock,
          type:       "simple",
          isTomorrow: true,
        };
      });

    const enriched = customScheduleItems.map((item) => {
      const isPrayer = /שחרית|מנחה|ערבית|מעריב|תפילה|תפילת/.test(item.title || "");
      const defaultColor = isPrayer ? prayerColor : lessonColor;
      return {
        ...item,
        icon: item.icon || Users,
        accentColor: item.accentColor || defaultColor,
        type: item.type || "highlighted",
      };
    });

    return [...base, ...baseTomorrow, ...enriched];
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zmanimMap, customScheduleItems, prayerColor, lessonColor, iconMap]);

  const { processedItems, pastHighlightedItems, futureHighlightedItems, nowItemIndex, currentTimeStr, currentItemMatch, currentTotalMinutes } = useMemo(() => {
    const currentTimeStr = now.toLocaleTimeString("he-IL", {
      hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false,
    });
    const currentHour = now.getHours();
    const currentMinutes = now.getMinutes();

    // Default: virtual chatzot at 01:30 → startOfDay = 02:00
    // Matches Timeline.jsx behavior: treats 00:00–01:59 as "late tonight" not "this morning"
    let chatzotNightHour = 1;
    let chatzotNightMinute = 30;
    if (zmanimMap?.chatzotNight && zmanimMap.chatzotNight !== '--:--') {
      const [h, m] = zmanimMap.chatzotNight.split(":").map(Number);
      if (!isNaN(h) && !isNaN(m)) {
        chatzotNightHour = h;
        chatzotNightMinute = m;
      }
    }

    let startOfDayHour = chatzotNightHour;
    let startOfDayMinute = chatzotNightMinute + 30;
    if (startOfDayMinute >= 60) { startOfDayMinute -= 60; startOfDayHour += 1; }
    if (startOfDayHour >= 24)  { startOfDayHour -= 24; }

    const startOfDayTotalMinutes = startOfDayHour * 60 + startOfDayMinute;
    const currentTotalMinutesRaw = currentHour * 60 + currentMinutes;

    let adjustedCurrentMinutes;
    let isAfterStartOfDay = false;

    if (startOfDayHour >= 0 && startOfDayHour < 12) {
      if (currentHour >= 0 && currentHour < 12) {
        if (currentTotalMinutesRaw >= startOfDayTotalMinutes) {
          isAfterStartOfDay = true;
          adjustedCurrentMinutes = currentTotalMinutesRaw - startOfDayTotalMinutes;
        } else {
          adjustedCurrentMinutes = (24 * 60 - startOfDayTotalMinutes) + currentTotalMinutesRaw;
        }
      } else {
        adjustedCurrentMinutes = currentTotalMinutesRaw - startOfDayTotalMinutes;
      }
    } else {
      if (currentHour >= 0 && currentHour < 23) {
        isAfterStartOfDay = true;
        adjustedCurrentMinutes = (24 * 60 - startOfDayTotalMinutes) + currentTotalMinutesRaw;
      } else {
        if (currentTotalMinutesRaw >= startOfDayTotalMinutes) {
          isAfterStartOfDay = true;
          adjustedCurrentMinutes = currentTotalMinutesRaw - startOfDayTotalMinutes;
        } else {
          adjustedCurrentMinutes = (24 * 60 - startOfDayTotalMinutes) + currentTotalMinutesRaw;
        }
      }
    }

    const currentTotalMinutes = adjustedCurrentMinutes;
    const dayLength = 24 * 60;
    const minTime = 0;
    const maxTime = dayLength;

    if (isAfterStartOfDay) {
      adjustedCurrentMinutes = 0;
    }

    const itemsWithTime = scheduleItems.map((item, index) => {
      const [hours, mins] = item.time.split(":").map(Number);
      const itemTotalMinutesRaw = hours * 60 + mins;
      let adjustedItemMinutes;

      // Times in the midnight hour (00:xx) always belong to "late tonight" —
      // force wrap path so they're never marked as past during the day/evening
      if (hours === 0 && !item.isTomorrow) {
        adjustedItemMinutes = (24 * 60 - startOfDayTotalMinutes) + itemTotalMinutesRaw;
      } else if (startOfDayHour >= 0 && startOfDayHour < 12) {
        adjustedItemMinutes = itemTotalMinutesRaw >= startOfDayTotalMinutes
          ? itemTotalMinutesRaw - startOfDayTotalMinutes
          : (24 * 60 - startOfDayTotalMinutes) + itemTotalMinutesRaw;
      } else {
        adjustedItemMinutes = itemTotalMinutesRaw >= startOfDayTotalMinutes
          ? itemTotalMinutesRaw - startOfDayTotalMinutes
          : (24 * 60 - startOfDayTotalMinutes) + itemTotalMinutesRaw;
      }

      if (item.isTomorrow) adjustedItemMinutes += 24 * 60;

      return { ...item, realTime: adjustedItemMinutes, index };
    }).sort((a, b) => a.realTime - b.realTime);

    const filteredItems = itemsWithTime.filter(
      (item) => item.realTime >= minTime && item.realTime <= maxTime
    );

    const pastHighlightedItems = [];
    const futureHighlightedItems = [];

    let nowItemIndex = filteredItems.length;
    for (let i = 0; i < filteredItems.length; i++) {
      if (currentTotalMinutes < filteredItems[i].realTime) {
        nowItemIndex = i;
        break;
      }
    }

    const currentTimeFormatted = `${String(currentHour).padStart(2, "0")}:${String(currentMinutes).padStart(2, "0")}`;
    const currentItemMatch = filteredItems.find((item) => item.time === currentTimeFormatted) || null;

    return {
      processedItems: filteredItems,
      pastHighlightedItems,
      futureHighlightedItems,
      nowItemIndex,
      currentTimeStr,
      currentItemMatch,
      currentTotalMinutes,
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [now, scheduleItems]);

  return {
    processedItems,
    pastHighlightedItems,
    futureHighlightedItems,
    nowItemIndex,
    currentTimeStr,
    currentItemMatch,
    currentTotalMinutes,
    dayWithHebrewDate,
  };
}
